/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-data-processing-log.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { makeStyles } from 'tss-react/mui';
import { useTranslation, useBoolean, useGridCells } from 'hooks';
import { GET_IMPORT_LOGS, GET_IMPORTS_FOR_STUDY } from 'queries/central-monitoring';
import { ImportAction } from 'types/graphql-global-types';
import { CustomDialog, DialogContent } from 'views/components/core/dialog';
import ExpandableGrid from 'views/components/core/expandable/grid';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';
import './admin-study-data-processing-log.scss';
import Constants from '@utils/constants';
import { getImportsForStudy_OpraQueries_importsForStudy_DataLoad } from '@queries/generated/central-monitoring.generated';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

// makeStyles is deprecated in MUI v5, but still works until later upgrades
// It leads to errors below of the type "Property 'foo' does not exist on type 'ClassNameMap<never>'"
// (because createStyles now returns never to mark it as deprecated)
// With MUI v5, we should be using the new styled API or the sx prop instead.
// This may be required if we ever need to upgrade again, but hopefully we can avoid this
// (We only needed to upgrade in 2026.1 because React 19 required an upgrade to Mui v5)
// https://mui.com/material-ui/migration/migrating-from-jss/
const useStyles = makeStyles()(() => ({
    headerTopSticky: {
        position: 'sticky',
        top: -20,
        paddingTop: 10,
        paddingBottom: 10,
        zIndex: 100,
        backgroundColor: 'white',
        marginLeft: -1,
        marginRight: -1,
    },
    closeBtn: {
        position: 'absolute',
        top: 20,
        right: 20,
        fontSize: 26,
        cursor: 'pointer',
    },
}));

const CM2ImportActions = {
    StartImportData: 'StartImportData',
    FinishImportData: 'FinishImportData',
    FinishGenerateFields: 'FinishGenerateFields',
    FailedToGenerateFields: 'FailedToGenerateFields',
    FinishProcessPanels: 'FinishProcessPanels',
    FailedToProcessPanels: 'FailedToProcessPanels',
    RollbackProcessPanels: 'RollbackProcessPanels',
    RollbackGenerateFields: 'RollbackGenerateFields',
    RollbackImport: 'RollbackImport',
    FailedToRollbackPanels: 'FailedToRollbackPanels',
};

const CM2ImportState = {
    ImportingData: 'ImportingData',
    DataImported: 'DataImported',
    FieldsGenerated: 'FieldsGenerated',
    Complete: 'Complete',
    RolledBack: 'RolledBack',
    RollbackPanelsFailed: 'RollbackPanelsFailed',
};

const AdminStudyDataProcessingLog = ({ study }: any) => {
    const { DateCell, DateTimeCell } = useGridCells();

    const { data: cm2Data, isLoading } = DatasetApi.useGetDataProcessingLogs(study.id as string);

    const { classes } = useStyles();
    const { t } = useTranslation();
    const viewingLog = useBoolean(false);
    const [loadImports, { loading, error, data }] = useLazyQuery(GET_IMPORTS_FOR_STUDY);
    const [loadLogs, { loading: loadingLogs, data: logData }] = useLazyQuery(GET_IMPORT_LOGS);
    const [logs, setLogs] = useState([]);

    useEffect(() => {
        if (study) loadImports({ variables: { studyId: study.id } });
    }, [study]);

    useEffect(() => {
        if (logData?.logsForImport) setLogs(logData?.logsForImport);
    }, [logData]);

    if (!study) return <h3>{t('common:noStudySelected')}</h3>;

    if (error) return <Error error={error} />;

    if (loading || !study || !data || isLoading) return <Loading />;

    const getActionType = (action: ImportAction) => {
        switch (action) {
            case ImportAction.FINISH_GENERATE_FIELDS:
            case ImportAction.FAILED_TO_GENERATE_FIELDS:
            case ImportAction.ROLLBACK_GENERATE_FIELDS:
                return t('cm:calculatedFields');

            case ImportAction.FINISH_PROCESS_PANELS:
            case ImportAction.FAILED_TO_PROCESS_PANELS:
            case ImportAction.ROLLBACK_PROCESS_PANELS:
                return t('cm:modelData');

            case ImportAction.START_IMPORT_DATA:
            case ImportAction.FINISH_IMPORT_DATA:
            case ImportAction.ROLLBACK_IMPORT:
                return t('cm:dataLoad');

            default:
                return '';
        }
    };

    const isActionHasDataLoadType = (action: ImportAction) => {
        if (
            action === ImportAction.START_IMPORT_DATA ||
            action === ImportAction.FINISH_IMPORT_DATA ||
            action === ImportAction.ROLLBACK_IMPORT
        )
            return true;

        return false;
    };

    const getAction = (action: ImportAction) => {
        switch (action) {
            case ImportAction.FAILED_TO_GENERATE_FIELDS:
            case ImportAction.FAILED_TO_PROCESS_PANELS:
                return t('cm:error');

            case ImportAction.START_IMPORT_DATA:
                return t('cm:started');

            case ImportAction.FINISH_GENERATE_FIELDS:
            case ImportAction.FINISH_IMPORT_DATA:
            case ImportAction.FINISH_PROCESS_PANELS:
                return t('cm:completed');

            case ImportAction.ROLLBACK_GENERATE_FIELDS:
            case ImportAction.ROLLBACK_IMPORT:
            case ImportAction.ROLLBACK_PROCESS_PANELS:
                return t('cm:rolledBack');

            default:
                return '';
        }
    };

    const cm2Action = (action: string | null | undefined) => {
        switch (action) {
            case CM2ImportActions.FailedToGenerateFields:
            case CM2ImportActions.FailedToProcessPanels:
            case CM2ImportActions.FailedToRollbackPanels:
                return t('cm:error');

            case CM2ImportActions.StartImportData:
                return t('cm:started');

            case CM2ImportActions.FinishGenerateFields:
            case CM2ImportActions.FinishImportData:
            case CM2ImportActions.FinishProcessPanels:
                return t('cm:completed');

            case CM2ImportActions.RollbackGenerateFields:
            case CM2ImportActions.RollbackImport:
            case CM2ImportActions.RollbackProcessPanels:
                return t('cm:rolledBack');

            default:
                return '';
        }
    };

    const cm2ActionType = (action: string | null | undefined) => {
        switch (action) {
            case CM2ImportActions.StartImportData:
            case CM2ImportActions.FinishImportData:
            case CM2ImportActions.RollbackImport:
                return t('cm:dataLoad');

            case CM2ImportActions.FinishGenerateFields:
            case CM2ImportActions.RollbackGenerateFields:
            case CM2ImportActions.FailedToGenerateFields:
                return t('cm:calculatedFields');

            case CM2ImportActions.FinishProcessPanels:
            case CM2ImportActions.FailedToProcessPanels:
            case CM2ImportActions.RollbackProcessPanels:
            case CM2ImportActions.FailedToRollbackPanels:
                return t('cm:modelData');

            default:
                return '';
        }
    };

    // The state is only used to see which step is the data import complete step. This just needs to match the value
    // provided by GraphQL for the legacy steps
    const cm2State = (state: string | null | undefined) => {
        switch (state) {
            case CM2ImportState.DataImported:
                return 'DATA_IMPORTED';
            case CM2ImportState.ImportingData:
            case CM2ImportState.Complete:
            case CM2ImportState.FieldsGenerated:
            case CM2ImportState.RollbackPanelsFailed:
            case CM2ImportState.RolledBack:
            default:
                return '';
        }
    };

    interface StepsType {
        action: string;
        date: any;
        actionType: string;
        completedBy: string;
        logIds: string[];
    }
    interface DataItem {
        id: any;
        dataSetDate: any;
        dataLoadDate: any;
        latestAction: string;
        lastActionDate: any;
        completedBy: string;
        cmVersion: string;
        isLatest: boolean;
        steps: StepsType[];
    }

    const items: DataItem[] = (data.importsForStudy ?? [])
        .filter((d: getImportsForStudy_OpraQueries_importsForStudy_DataLoad | null) => d !== null)
        .map((item: getImportsForStudy_OpraQueries_importsForStudy_DataLoad) => {
            const legacySteps = (item.steps ?? []).map((step: any) => ({
                ...step,
                actionType: getActionType(step.action),
                action: getAction(step.action),
                originalAction: step.action,
            }));

            const cm2Dataset = cm2Data?.find((d) => d.dataset.dataSetDate === item.dataCutDate);
            const cm2Steps = (cm2Dataset?.importSteps ?? []).map((step) => {
                return {
                    ...step,
                    action: cm2Action(step.action),
                    actionType: cm2ActionType(step.action),
                    state: cm2State(step.state),
                };
            });

            // If dataset object ID is empty then use CM2 methods for display
            const isLatest = item.dataSetId === Constants.ObjectId.Empty;

            const steps = [...legacySteps, ...cm2Steps].sort((a, b) => {
                return new Date(b.date).getTime() - new Date(a.date).getTime();
            });
            const latestAction = steps.length > 0 ? `${steps[0].actionType} - ${steps[0].action}` : '';

            return {
                id: item.id,
                dataSetDate: item.dataCutDate,
                dataLoadDate: steps.find((step: any) => step.state === 'DATA_IMPORTED')?.date,
                latestAction,
                lastActionDate: steps.length > 0 ? item.steps[0].date : null,
                completedBy: steps.length > 0 ? item.steps[0].completedBy : '',
                cmVersion: isLatest ? t('cm:latestVersion') : t('cm:legacy'),
                isLatest,
                steps,
            };
        });

    return (
        <>
            <PageTitle text={t('cm:dataProcessingLog')} />
            <div style={{ marginBottom: 40 }} />
            <ExpandableGrid
                customKey="study-team-grid"
                id="studyTeamGrid"
                key="study-team-grid"
                items={items}
                rowActions={null}
                groupable={false}
                showPager
                scrollable="scrollable"
                reorderable
                pageable={false}
                showFirstColumn
                addScroll
                resizable
                options={{
                    allowExcelExport: false,
                    allowExpandCollapseAll: true,
                    useFilterChips: true,
                }}
                detail={(item: any) => {
                    const steps = item.steps ?? [];

                    const closeLog = () => {
                        viewingLog.setFalse();
                        setLogs(null);
                    };
                    return (
                        <>
                            <CustomDialog
                                className="data-processing-log-dialog"
                                onClose={closeLog}
                                fullWidth
                                scroll="paper"
                                maxWidth="lg"
                                open={viewingLog.value}
                            >
                                <DialogContent style={{ position: 'relative' }}>
                                    <div className={classes.headerTopSticky}>
                                        <FontAwesomeIcon
                                            icon={['fal', 'times']}
                                            className={classes.closeBtn}
                                            onClick={closeLog}
                                            data-testid="close-dialog"
                                        />
                                        <PageTitle text="Data Processing Log" />
                                    </div>
                                    <br />
                                    <div className="mb-30">
                                        {loadingLogs ? (
                                            <Loading />
                                        ) : (
                                            (logs ?? []).map((logEntry: any) => (
                                                <div key={logEntry.id}>
                                                    <pre>{logEntry.log}</pre>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </DialogContent>
                            </CustomDialog>
                            <div className="child-grid-wrapper" key="data-processing-grid-wrapper">
                                <ExpandableGrid
                                    isChildGrid
                                    customKey="data-processing-steps"
                                    items={steps}
                                    groupable={false}
                                    showPager
                                    scrollable="scrollable"
                                    reorderable
                                    resizable
                                    pageable={false}
                                    rowActions={(row) =>
                                        row.logIds?.length && !isActionHasDataLoadType(row.originalAction)
                                            ? [
                                                  {
                                                      label: 'cm:viewLog',
                                                      icon: ['fal', 'eye'],
                                                      onClick: () => {
                                                          setLogs(null);
                                                          if (!item.isLatest)
                                                              loadLogs({ variables: { logIds: row.logIds } });
                                                          viewingLog.setTrue();
                                                      },
                                                      enabled: true,
                                                  },
                                              ]
                                            : null
                                    }
                                    options={{
                                        allowExcelExport: false,
                                        allowExpandCollapseAll: false,
                                        useFilterChips: false,
                                    }}
                                    columns={[
                                        {
                                            label: t('cm:actionType'),
                                            field: 'actionType',
                                            minWidth: 230,
                                        },
                                        {
                                            label: t('cm:runDate'),
                                            field: 'date',
                                            minWidth: 140,
                                            cells: { data: DateTimeCell() },
                                            filter: 'date',
                                        },
                                        {
                                            label: t('cm:action'),
                                            field: 'action',
                                            minWidth: 230,
                                        },
                                        {
                                            label: t('cm:completedBy'),
                                            field: 'completedBy',
                                            minWidth: 230,
                                        },
                                    ]}
                                />
                            </div>
                        </>
                    );
                }}
                columns={[
                    {
                        label: t('cm:dataSetDate'),
                        field: 'dataSetDate',
                        minWidth: 170,
                        cells: { data: DateCell },
                        filter: 'date',
                    },
                    {
                        label: t('cm:dataLoadDate'),
                        field: 'dataLoadDate',
                        minWidth: 185,
                        cells: { data: DateCell },
                        filter: 'date',
                    },
                    {
                        label: t('cm:latestAction'),
                        field: 'latestAction',
                        minWidth: 230,
                    },
                    {
                        label: t('cm:lastActionDate'),
                        field: 'lastActionDate',
                        minWidth: 200,
                        cells: { data: DateTimeCell() },
                        filter: 'date',
                    },
                    {
                        label: t('cm:completedBy'),
                        field: 'completedBy',
                        minWidth: 230,
                    },
                    {
                        label: t('cm:cmVersion'),
                        field: 'cmVersion',
                        minWidth: 180,
                    },
                ]}
            />
        </>
    );
};

export default AdminStudyDataProcessingLog;
